import React from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import Header from './Header';
import Sidebar from './Sidebar';
import ChatResizeHandle from './ChatResizeHandle';
import PageBreadcrumbs from './PageBreadcrumbs';
import NotionStyleChatPanel from '../chat/NotionStyleChatPanel';
import { useShellLayout } from '@hooks/useShellLayout';
import { useShellActions } from '@hooks/useShellActions';
import { useThemeClass } from '@hooks/useThemeClass';
import { useDocumentLanguage } from '@hooks/useDocumentLanguage';
import { useChatResize } from '@hooks/useChatResize';
import { useAppSelector } from '@store/index';
import { selectCadThicknessEnabled } from '@features/featureFlags/featureFlagsSlice';
import { isThicknessViewerLocation } from '@utils/cadViewerMode';
import { TooltipProvider } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

const MainLayout: React.FC = () => {
  const layout = useShellLayout();
  const actions = useShellActions(layout);
  const resize = useChatResize(layout.chatWidth);
  useThemeClass();
  useDocumentLanguage();

  // The CAD thickness workspace takes the full width, so the chat column is
  // hidden and its toggle removed while that view is active (ported from main).
  const location = useLocation();
  const cadThicknessEnabled = useAppSelector(selectCadThicknessEnabled);
  const thicknessMode =
    cadThicknessEnabled === true &&
    isThicknessViewerLocation(location.pathname, location.search);

  const hasChatColumn = layout.hasChatColumn && !thicknessMode;
  const chatVisible = layout.chatVisible && !thicknessMode;
  const chatWidth = hasChatColumn ? resize.width : layout.chatWidth;

  return (
    <TooltipProvider>
    <div
      className="revy-ui revy-shell bg-background text-foreground antialiased"
      data-resizing={resize.isResizing}
      style={
        {
          '--rail-w': `${layout.railWidth}px`,
          '--chat-w': `${hasChatColumn ? chatWidth : 0}px`,
        } as React.CSSProperties
      }
    >
      <div data-shell="rail" aria-hidden="true" />

      {/* Ahead of <main> in the DOM so tabbing reaches the primary navigation
          before the page body. The grid places it visually regardless. */}
      <Sidebar
        collapsed={!layout.isRailExpanded}
        isMobile={layout.isMobile}
        drawerOpen={layout.navDrawerOpen}
        onCloseDrawer={actions.closeNav}
        onToggle={actions.toggleNav}
      />

      <div data-shell="header" className="min-w-0">
        <Header
          chatOpen={chatVisible}
          chatAvailable={!thicknessMode}
          onToggleNav={layout.isMobile ? actions.toggleNav : undefined}
          onToggleChat={actions.toggleChat}
        />
      </div>

      <main
        data-shell="main"
        className={cn(
          'min-w-0',
          thicknessMode
            ? 'px-2 py-2 sm:px-4 sm:py-3'
            : 'px-4 py-6 sm:px-6 lg:px-8',
        )}
      >
        <div
          data-shell="content"
          className={cn(
            'min-w-0',
            thicknessMode && 'flex h-full min-h-0 flex-col',
            layout.contentMaxWidth && 'mx-auto',
          )}
          style={layout.contentMaxWidth ? { maxWidth: layout.contentMaxWidth } : undefined}
        >
          <PageBreadcrumbs className={thicknessMode ? 'mb-2 shrink-0' : location.pathname === '/drbfm'
            ? 'mx-auto mb-4 w-full max-w-[1200px] px-6 pt-4' : 'mb-4'} />
          <Outlet />
        </div>
      </main>

      <div data-shell="chat" aria-hidden="true" />

      {hasChatColumn && (
        <ChatResizeHandle chatWidth={chatWidth} handleProps={resize.handleProps} />
      )}

      <NotionStyleChatPanel
        open={chatVisible}
        onClose={actions.toggleChat}
        width={chatWidth}
        isOverlay={!layout.chatDocked}
      />
    </div>
    </TooltipProvider>
  );
};

export default MainLayout;
